export interface SchemaConfig {
  domain: string;
  siteName: string;
  locale: string;
}

const defaultConfig: SchemaConfig = {
  domain: "https://igetalibar.net",
  siteName: "IGET Australia",
  locale: "en-AU",
};

export function generateOrganizationSchema(config: SchemaConfig = defaultConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "IGET Australia",
    url: config.domain,
    logo: `${config.domain}/logo.png`,
    description: "IGET product information, flavour guidance, and Australian customer support resources.",
    sameAs: ["https://twitter.com/igetalibargeo", "https://www.facebook.com/igetalibargeo", "https://www.instagram.com/igetalibargeo"],
    areaServed: { "@type": "Country", name: "AU" },
    contactPoint: { "@type": "ContactPoint", contactType: "Customer Service", areaServed: "AU", email: "support@igetalibar.com" },
  };
}

export function generateProductSchema(product: { name: string; description: string; brand: string; image: string; rating?: number; reviewCount?: number; priceLow?: number; priceHigh?: number; currency?: string; offerCount?: number; url: string }, _config: SchemaConfig = defaultConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: product.image,
    brand: { "@type": "Brand", name: product.brand || "IGET" },
    url: product.url,
    ...(product.rating ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating.toString(), reviewCount: product.reviewCount || "0" } } : {}),
    ...(product.priceLow ? { offers: { "@type": "AggregateOffer", priceCurrency: product.currency || "AUD", lowPrice: product.priceLow.toString(), highPrice: product.priceHigh || product.priceLow, offerCount: product.offerCount || "1" } } : {}),
  };
}

export function generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>, config: SchemaConfig = defaultConfig) {
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: breadcrumbs.map((item, index) => ({ "@type": "ListItem", position: index + 1, name: item.name, item: item.url.startsWith("http") ? item.url : `${config.domain}${item.url}` })) };
}

export function generateLocalBusinessSchema(config: SchemaConfig = defaultConfig) {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "IGET Australia",
    url: config.domain,
    description: "IGET product information and customer support resources for Australia.",
    address: { "@type": "PostalAddress", addressCountry: "AU", addressRegion: "NSW", addressLocality: "Sydney" },
    geo: { "@type": "GeoCoordinates", latitude: "-33.8688", longitude: "151.2093" },
    areaServed: ["New South Wales", "Victoria", "Queensland", "Western Australia", "South Australia", "Tasmania", "Australian Capital Territory", "Northern Territory"].map((name) => ({ "@type": "State", name })),
    priceRange: "AUD 15 - AUD 129",
  };
}

export function generateFAQPageSchema(faqs: Array<{ question: string; answer: string }>) {
  return { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.question, acceptedAnswer: { "@type": "Answer", text: faq.answer } })) };
}

export function generateWebsiteSchema(config: SchemaConfig = defaultConfig) {
  return { "@context": "https://schema.org", "@type": "WebSite", name: config.siteName, url: config.domain, potentialAction: { "@type": "SearchAction", target: { "@type": "EntryPoint", urlTemplate: `${config.domain}/search?q={search_term_string}` }, query_input: "required name=search_term_string" } };
}

export function generateHowToSchema(title: string, description: string, steps: Array<{ name: string; description: string; image?: string }>) {
  return { "@context": "https://schema.org", "@type": "HowTo", name: title, description, step: steps.map((step, index) => ({ "@type": "HowToStep", position: index + 1, name: step.name, text: step.description, ...(step.image ? { image: step.image } : {}) })) };
}

export function schemaToJsonLd(schema: unknown): string { return JSON.stringify(schema); }
export function createSchemaScript(schema: unknown): string { return `<script type="application/ld+json">${schemaToJsonLd(schema)}</script>`; }
